/**
 * Context for managing sysadmin user emulation state.
 *
 * When a sysadmin emulates a user, they receive a special JWT token that
 * allows them to act as that user while maintaining full audit trails.
 *
 * Unlike ActingAsContext (per-request header), emulation uses JWT tokens
 * and is session-based.
 */
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { apiPost } from '../api/axiosInstance';
import { authApi } from '../auth/authApi';
import { offlineStorage } from '../offline/OfflineStorageService';
import { persistKnownLanguage, clearKnownLanguageCookie } from '../../i18n';
// Leaf module — imported directly (not via the `shared/auth` barrel) because
// AuthContext imports this file's `clearEmulationData`.
import { AUTH_STORAGE_KEYS, EMULATION_STORAGE_KEYS } from '../auth/storageKeys';

// The app UI language is a single global localStorage key (i18n's
// `lookupLocalStorage`), NOT scoped per user. Without save/restore it leaks
// across the emulation boundary: a language change made while emulating would
// stick on the sysadmin's own session after ending. Mirror the token/user
// save+restore so the sysadmin's language survives an emulation round-trip.
const LANGUAGE_KEY = 'panluma_language';

/**
 * Clear all emulation data from localStorage.
 * Call this when user logs out or session expires to prevent
 * showing emulation banner on login page.
 */
export function clearEmulationData(): void {
  localStorage.removeItem(EMULATION_STORAGE_KEYS.session);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.token);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.originalToken);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.originalUser);
}

export interface EmulationSession {
  session_id: string;
  sysadmin_user_id: string;
  sysadmin_email: string;
  emulated_user_id: string;
  emulated_user_email: string;
  emulated_user_name: string;
  emulated_tenant_id: string;
  emulated_tenant_name: string;
  justification: string;
  authority: string;
  started_at: string;
  expires_at: string;
  is_active: boolean;
}

export interface ActivateEmulationOptions {
  /** Absolute URL to navigate to after emulation activates. May be same-origin or cross-origin. */
  landingUrl?: string;
}

interface EmulationContextType {
  // Current emulation session (null if not emulating)
  emulationSession: EmulationSession | null;
  // Whether we're in emulation mode
  isEmulating: boolean;
  // Start emulating a user (called after API returns token)
  activateEmulation: (
    session: EmulationSession,
    emulationToken: string,
    options?: ActivateEmulationOptions,
  ) => Promise<void>;
  // End emulation and return to sysadmin context
  endEmulation: () => Promise<void>;
  // Check if emulation is expired
  isEmulationExpired: () => boolean;
}

const EmulationContext = createContext<EmulationContextType | undefined>(undefined);

interface EmulationProviderProps {
  children: ReactNode;
}

export function EmulationProvider({ children }: EmulationProviderProps) {
  const [emulationSession, setEmulationSession] = useState<EmulationSession | null>(() => {
    // Initialize from localStorage (persists across tabs)
    const stored = localStorage.getItem(EMULATION_STORAGE_KEYS.session);
    if (stored) {
      try {
        const session = JSON.parse(stored);
        // Check if expired
        if (new Date(session.expires_at) < new Date()) {
          localStorage.removeItem(EMULATION_STORAGE_KEYS.session);
          localStorage.removeItem(EMULATION_STORAGE_KEYS.token);
          return null;
        }
        return session;
      } catch {
        return null;
      }
    }
    return null;
  });

  // Persist to localStorage when changed
  useEffect(() => {
    if (emulationSession) {
      localStorage.setItem(EMULATION_STORAGE_KEYS.session, JSON.stringify(emulationSession));
    } else {
      localStorage.removeItem(EMULATION_STORAGE_KEYS.session);
      localStorage.removeItem(EMULATION_STORAGE_KEYS.token);
    }
  }, [emulationSession]);

  // Check expiration periodically
  useEffect(() => {
    if (!emulationSession) return;

    const checkExpiration = () => {
      if (new Date(emulationSession.expires_at) < new Date()) {
        setEmulationSession(null);
        // Reload to restore sysadmin context
        window.location.href = '/';
      }
    };

    const interval = setInterval(checkExpiration, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [emulationSession]);

  const activateEmulation = useCallback(async (
    session: EmulationSession,
    emulationToken: string,
    options?: ActivateEmulationOptions,
  ) => {
    // Save original token for restoration (using the correct key that axiosInstance uses)
    const currentToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (currentToken) {
      localStorage.setItem(EMULATION_STORAGE_KEYS.originalToken, currentToken);
    }

    // Save original user data for restoration
    const currentUser = localStorage.getItem(AUTH_STORAGE_KEYS.user);
    if (currentUser) {
      localStorage.setItem(EMULATION_STORAGE_KEYS.originalUser, currentUser);
    }

    // Save the sysadmin's UI language so it can be restored when emulation ends
    // (the global panluma_language key would otherwise leak the emulated
    // session's language back onto the sysadmin). If the sysadmin had no
    // explicit language, endEmulation drops the override entirely.
    localStorage.setItem(EMULATION_STORAGE_KEYS.originalLanguage, localStorage.getItem(LANGUAGE_KEY) ?? '');

    // Determine if landingUrl is cross-origin; if so, hop via one-time code so the
    // emulation session is established on the target domain (banner + auth).
    const landingUrl = options?.landingUrl;
    let landingTarget = '/';
    let crossOriginTarget: { domain: string; path: string } | null = null;

    if (landingUrl) {
      try {
        const url = new URL(landingUrl, window.location.origin);
        if (url.origin === window.location.origin) {
          landingTarget = url.pathname + url.search + url.hash;
        } else {
          crossOriginTarget = {
            domain: url.host,
            path: url.pathname + url.search + url.hash,
          };
        }
      } catch {
        // Malformed landingUrl — fall back to default behavior
      }
    }

    if (crossOriginTarget) {
      // Cross-domain: create a one-time code carrying the emulation session, then
      // hop to the target domain's token-exchange page which restores state.
      const emulatedUser = {
        id: session.emulated_user_id,
        email: session.emulated_user_email,
        first_name: null,
        last_name: null,
        tenant_id: session.emulated_tenant_id,
        is_sysadmin: false,
      };
      try {
        const { code } = await authApi.createCrossDomainCode(
          crossOriginTarget.domain,
          emulationToken,
          emulationToken,
          emulatedUser as unknown as Parameters<typeof authApi.createCrossDomainCode>[3],
          session as unknown as Record<string, unknown>,
        );
        const nextParam = encodeURIComponent(crossOriginTarget.path);
        const protocol = window.location.protocol;
        window.location.href = `${protocol}//${crossOriginTarget.domain}/auth/token-exchange?code=${code}&next=${nextParam}`;
        return;
      } catch (error) {
        console.error('Failed to create cross-domain code for emulation:', error);
        // Fall through to same-origin activation as a best effort
      }
    }

    // Same-origin activation
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, emulationToken);
    localStorage.setItem(EMULATION_STORAGE_KEYS.token, emulationToken);

    // Clear the cached user data so AuthContext fetches fresh data for the emulated user
    localStorage.removeItem(AUTH_STORAGE_KEYS.user);

    // Update session state
    setEmulationSession(session);

    // Clear the persisted React Query cache to prevent showing sysadmin's cached data
    await offlineStorage.clearAllData();

    // Reload to refresh all data with new context
    window.location.href = landingTarget;
  }, []);

  const endEmulation = useCallback(async () => {
    try {
      // Call API to end session and get return token
      interface EndEmulationResponse {
        data: {
          return_token?: string;
        };
      }
      const response = await apiPost<EndEmulationResponse>('/api/v1/admin/emulation/end');
      const returnToken = response.data?.return_token;

      // Restore original token or use return token
      if (returnToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, returnToken);
      } else {
        const originalToken = localStorage.getItem(EMULATION_STORAGE_KEYS.originalToken);
        if (originalToken) {
          localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, originalToken);
        }
      }
    } catch (error) {
      console.error('Failed to end emulation:', error);
      // Restore original token on error
      const originalToken = localStorage.getItem(EMULATION_STORAGE_KEYS.originalToken);
      if (originalToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, originalToken);
      }
    }

    // Restore original user data
    const originalUser = localStorage.getItem(EMULATION_STORAGE_KEYS.originalUser);
    if (originalUser) {
      localStorage.setItem(AUTH_STORAGE_KEYS.user, originalUser);
    } else {
      // If no original user saved, clear user data so AuthContext fetches fresh
      localStorage.removeItem(AUTH_STORAGE_KEYS.user);
    }

    // Restore the sysadmin's UI language (undo any language change made while
    // emulating). The reload below lets i18n re-read panluma_language. Mirror the
    // restore onto the cross-subdomain cookie — which i18n now reads AHEAD of
    // localStorage — so the emulated user's language (stamped there by
    // UserLanguageSync during the session) doesn't briefly win on the sysadmin
    // after reload.
    const originalLanguage = localStorage.getItem(EMULATION_STORAGE_KEYS.originalLanguage);
    if (originalLanguage) {
      localStorage.setItem(LANGUAGE_KEY, originalLanguage);
      persistKnownLanguage(originalLanguage);
    } else {
      // Sysadmin had no explicit language — drop the emulated override so i18n
      // falls back to its own detection rather than keeping the emulated value.
      localStorage.removeItem(LANGUAGE_KEY);
      clearKnownLanguageCookie();
    }

    // Clean up emulation-specific storage
    localStorage.removeItem(EMULATION_STORAGE_KEYS.session);
    localStorage.removeItem(EMULATION_STORAGE_KEYS.token);
    localStorage.removeItem(EMULATION_STORAGE_KEYS.originalToken);
    localStorage.removeItem(EMULATION_STORAGE_KEYS.originalUser);
    localStorage.removeItem(EMULATION_STORAGE_KEYS.originalLanguage);
    setEmulationSession(null);

    // Clear the persisted React Query cache to prevent showing emulated user's cached data
    await offlineStorage.clearAllData();

    // Reload to refresh all data with sysadmin context
    window.location.href = '/';
  }, []);

  const isEmulationExpired = useCallback((): boolean => {
    if (!emulationSession) return false;
    return new Date(emulationSession.expires_at) < new Date();
  }, [emulationSession]);

  const value: EmulationContextType = {
    emulationSession,
    isEmulating: emulationSession !== null && !isEmulationExpired(),
    activateEmulation,
    endEmulation,
    isEmulationExpired,
  };

  return (
    <EmulationContext.Provider value={value}>
      {children}
    </EmulationContext.Provider>
  );
}

export function useEmulation(): EmulationContextType {
  const context = useContext(EmulationContext);
  if (!context) {
    throw new Error('useEmulation must be used within an EmulationProvider');
  }
  return context;
}

/**
 * Check if currently in emulation mode.
 * Use this in non-component code.
 */
export function isInEmulationMode(): boolean {
  const stored = localStorage.getItem(EMULATION_STORAGE_KEYS.session);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      return new Date(session.expires_at) > new Date();
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Get the current emulation session.
 * Use this in non-component code.
 */
export function getEmulationSession(): EmulationSession | null {
  const stored = localStorage.getItem(EMULATION_STORAGE_KEYS.session);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      if (new Date(session.expires_at) > new Date()) {
        return session;
      }
    } catch {
      return null;
    }
  }
  return null;
}
